import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
  Box,
  TextField,
  MenuItem,
  Snackbar,
  CircularProgress,
} from '@material-ui/core';
import { req } from '@omega-energia/react';
import { PartnerInvoiceStatus } from '@omega-energia/utilities';
import { DateTime } from 'luxon';
import { useForm, useWatch } from 'react-hook-form';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { useAuth } from '../../../../../auth/authProvider';
import { preventInvoiceNumberWhitespace, sanitizeInvoiceNumber } from './invoiceNumber.utils';

const buildDefaultValues = invoiceData => ({
  amount:
    invoiceData?.amount &&
    invoiceData?.amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }),
  invoiceNumber: sanitizeInvoiceNumber(invoiceData?.invoiceNumber) || '',
  status: invoiceData?.status,
  issueDate: invoiceData?.issueDate,
  referenceDate: invoiceData?.referenceDate,
  paymentDate: invoiceData?.paymentDate,
  uploadDate: invoiceData?.uploadDate,
  purchaseOrderNumber: invoiceData?.purchaseOrderNumber,
});

function ModalEditarDados({ isOpen, onClose, id, invoiceData, refetch }) {
  const { token } = useAuth();

  const previousStatusRef = useRef(invoiceData?.status);

  useEffect(() => {
    previousStatusRef.current = invoiceData?.status;
  }, [invoiceData]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    getValues,
    reset,
  } = useForm({
    defaultValues: buildDefaultValues(invoiceData),
  });

  const closeAndResetForm = () => {
    reset(buildDefaultValues(invoiceData));
    onClose();
  };

  const onSubmit = async data => {
    try {
      setIsLoading(true);

      delete data.amount;
      data.invoiceNumber = sanitizeInvoiceNumber(data.invoiceNumber);
      const prevStatus = invoiceData?.status;
      const nextStatus = data.status;

      const shouldSendToOracle =
        [PartnerInvoiceStatus.PENDENTE_ENVIO_NF_ORACLE, PartnerInvoiceStatus.FALHA_ENVIO_NF_ORACLE].includes(
          prevStatus,
        ) && nextStatus === PartnerInvoiceStatus.PROCESSO_DE_PAGAMENTO;

      const camposObrigatorios = [
        { nome: 'Número da NF', valor: data.invoiceNumber },
        { nome: 'Número do Pedido Oracle', valor: data.purchaseOrderNumber },
        { nome: 'Data da Emissão', valor: data.issueDate },
      ];

      const campoVazio = camposObrigatorios.find(c => !c.valor);

      if (campoVazio && shouldSendToOracle) {
        setSnackbar({
          open: true,
          message: `Campo obrigatório não preenchido: ${campoVazio.nome}`,
        });
        return;
      }

      await req.patch(`/backstage/comunidade/invoices/${id}`, data, { token });

      if (shouldSendToOracle) {
        try {
          await req.post(`/backstage/comunidade/invoices/${id}/send-to-oracle`, {}, { token });
        } catch (error) {
          await req.patch(
            `/backstage/comunidade/invoices/${id}`,
            {
              status: PartnerInvoiceStatus.FALHA_ENVIO_NF_ORACLE,
            },
            { token },
          );

          setValue('status', PartnerInvoiceStatus.FALHA_ENVIO_NF_ORACLE);

          setSnackbar({
            open: true,
            message: 'Erro ao enviar NF para Oracle.',
          });

          return;
        }
      }

      setSnackbar({
        open: true,
        message: 'Parceiro atualizado com sucesso!',
      });

      setTimeout(() => {
        closeAndResetForm();
        refetch();
      }, 2000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Houve um erro ao atualizar o parceiro',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const currentStatus = useWatch({ control, name: 'status' });
  const currentInvoiceNumber = useWatch({ control, name: 'invoiceNumber' }) || '';
  const { ref: invoiceNumberRef, name: invoiceNumberName, onBlur: onInvoiceNumberBlur } = register('invoiceNumber');

  return (
    <>
      <Dialog open={isOpen}>
        <DialogTitle>
          <Typography>Edição parceiro</Typography>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" flexDirection="row" mb={2} style={{ gap: '12px' }}>
              <TextField
                disabled
                variant="outlined"
                label="Valor Comissão"
                style={{ flex: 1 }}
                {...register('amount')}
                error={!!errors.amount}
                helperText={errors.amount?.message?.toString()}
              />
              <TextField
                variant="outlined"
                label="Número do Pedido Oracle"
                style={{ flex: 1 }}
                {...register('purchaseOrderNumber', {
                  validate: value => {
                    if (
                      [
                        PartnerInvoiceStatus.PENDENTE_VALIDACAO_ORACLE,
                        PartnerInvoiceStatus.AGUARDANDO_ENVIO_NF,
                        PartnerInvoiceStatus.PROCESSO_DE_PAGAMENTO,
                        PartnerInvoiceStatus.PENDENTE_ENVIO_NF_ORACLE,
                        PartnerInvoiceStatus.PAGAMENTO_NAO_ENVIADO,
                        PartnerInvoiceStatus.PAGO,
                        PartnerInvoiceStatus.NOTA_RECUSADA,
                      ].includes(currentStatus) &&
                      !value
                    ) {
                      return 'Este campo é obrigatório';
                    }
                    return true;
                  },
                })}
                error={!!errors.purchaseOrderNumber}
                helperText={errors.purchaseOrderNumber?.message?.toString()}
              />
            </Box>
            <Box display="flex" flexDirection="row" mb={2} style={{ gap: '12px' }}>
              <TextField
                variant="outlined"
                label="Número da NF"
                style={{ flex: 1 }}
                name={invoiceNumberName}
                inputRef={invoiceNumberRef}
                onBlur={onInvoiceNumberBlur}
                value={currentInvoiceNumber}
                onKeyDown={preventInvoiceNumberWhitespace}
                onChange={event =>
                  setValue('invoiceNumber', sanitizeInvoiceNumber(event.target.value), {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                error={!!errors.invoiceNumber}
                helperText={errors.invoiceNumber?.message?.toString()}
              />
              <TextField
                select
                variant="outlined"
                label="Status Pagamento"
                style={{ flex: 1 }}
                value={getValues('status')}
                onChange={e => setValue('status', e.target.value, { shouldValidate: true, shouldDirty: true })}
                error={!!errors.status}
                helperText={errors.status?.message?.toString()}
              >
                {Object.values(PartnerInvoiceStatus).map(status => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box display="flex" flexDirection="row" mb={2} style={{ gap: '12px' }}>
              <KeyboardDatePicker
                disableToolbar
                label="Data da Emissão"
                format="dd/MM/yyyy"
                value={getValues('issueDate') ? DateTime.fromISO(getValues('issueDate')).toJSDate() : null}
                onChange={date => {
                  if (date) {
                    const formattedDate = DateTime.fromISO(date).toFormat('yyyy-MM-dd');
                    setValue('issueDate', formattedDate, { shouldValidate: true, shouldDirty: true });
                  } else {
                    setValue('issueDate', '', { shouldValidate: true, shouldDirty: true });
                  }
                }}
                error={!!errors.issueDate}
                helperText={errors.issueDate?.message?.toString()}
              />
              <KeyboardDatePicker
                disableToolbar
                disabled
                label="Mês Competência"
                format="dd/MM/yyyy"
                value={getValues('referenceDate') ? DateTime.fromISO(getValues('referenceDate')).toJSDate() : null}
                onChange={date => {
                  if (date) {
                    const formattedDate = DateTime.fromISO(date).toFormat('yyyy-MM-dd');
                    setValue('referenceDate', formattedDate, { shouldValidate: true, shouldDirty: true });
                  } else {
                    setValue('referenceDate', '', { shouldValidate: true, shouldDirty: true });
                  }
                }}
                error={!!errors.referenceDate}
                helperText={errors.referenceDate?.message?.toString()}
              />
            </Box>
            <Box display="flex" flexDirection="row" mb={2} style={{ gap: '12px' }}>
              <KeyboardDatePicker
                disableToolbar
                label="Data pagamento"
                format="dd/MM/yyyy"
                value={getValues('paymentDate') ? DateTime.fromISO(getValues('paymentDate')).toJSDate() : null}
                onChange={date => {
                  if (date) {
                    const formattedDate = DateTime.fromISO(date).toFormat('yyyy-MM-dd');
                    setValue('paymentDate', formattedDate, { shouldValidate: true, shouldDirty: true });
                  } else {
                    setValue('paymentDate', '', { shouldValidate: true, shouldDirty: true });
                  }
                }}
                error={!!errors.paymentDate}
                helperText={errors.paymentDate?.message?.toString()}
              />
              <KeyboardDatePicker
                disableToolbar
                label="Entrada no sistema"
                format="dd/MM/yyyy"
                value={getValues('uploadDate') ? DateTime.fromISO(getValues('uploadDate')).toJSDate() : null}
                onChange={date => {
                  if (date) {
                    const formattedDate = DateTime.fromISO(date).toFormat('yyyy-MM-dd');
                    setValue('uploadDate', formattedDate, { shouldValidate: true, shouldDirty: true });
                  } else {
                    setValue('uploadDate', '', { shouldValidate: true, shouldDirty: true });
                  }
                }}
                error={!!errors.uploadDate}
                helperText={errors.uploadDate?.message?.toString()}
              />
            </Box>
            <Box display="flex" flexDirection="row" mb={2}>
              <TextField
                variant="outlined"
                label="Observação"
                {...register('comment')}
                placeholder="Observação"
                style={{ flex: 1 }}
              />
            </Box>
            <DialogActions>
              <Button onClick={closeAndResetForm} color="primary" disabled={isLoading}>
                Cancelar
              </Button>
              <Button type="submit" color="primary" disabled={isLoading || !isDirty} style={{ minWidth: '75px' }}>
                {isLoading ? <CircularProgress size="14px" /> : 'Salvar'}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        message={snackbar.message}
        onClose={() => setSnackbar({ open: false, message: '' })}
      />
    </>
  );
}

ModalEditarDados.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};

export default ModalEditarDados;
